import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import Swal from "sweetalert2";

const AddressModal = ({ closeModal }) => {
  const userId = localStorage.getItem("user_id");
  const token = localStorage.getItem("token");

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    house_no: "",
    road_name_area: "",
    land_mark: "",
    pin_code: "",
    state: "",
    city: "",
    district: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;

    if ((name === "mobile" || name === "pin_code") && value) {
      // Only digits allowed
      if (!/^\d*$/.test(value)) {
        return;
      }

      // Length restrictions
      if (
        (name === "mobile" && value.length > 10) ||
        (name === "pin_code" && value.length > 6)
      ) {
        return;
      }
    }

    setFormData({ ...formData, [name]: value });

    // Remove error on typing
    if (value.trim() !== "") {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  useEffect(() => {
    // Fetch states
    const fetchStates = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/state-list`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await response.json();
        if (data.status === "1") setStates(data.data);
      } catch (err) {
        console.error("State list fetch error:", err);
      }
    };
    fetchStates();
  }, [token]);

  useEffect(() => {
    if (!formData.state) {
      setCities([]);
      setFormData({ ...formData, city: "" });
      return;
    }
    const fetchCities = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/city-list`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ state_id: formData.state }),
          }
        );
        const data = await response.json();
        if (data.status === "1") setCities(data.data);
      } catch (err) {
        console.error("City list fetch error:", err);
      }
    };
    fetchCities();
  }, [formData.state, token]);

  const validate = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      if (!formData[key] || formData[key].trim() === "") {
        newErrors[key] = "This field is required";
      }
    });

    // Mobile must be 10 digits
    if (formData.mobile && formData.mobile.length !== 10) {
      newErrors.mobile = "Mobile number must be 10 digits";
    }

    // Pincode must be 6 digits
    if (formData.pin_code && formData.pin_code.length !== 6) {
      newErrors.pin_code = "Pincode must be 6 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const payload = {
        user_id: userId,
        name: formData.name,
        mobile: formData.mobile,
        house_no: formData.house_no,
        road_name_area: formData.road_name_area,
        land_mark: formData.land_mark,
        pin_code: formData.pin_code,
        state: formData.state,
        city: formData.city,
        district: formData.district,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/user-address-create`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success === "1") {
        Swal.fire({
          icon: "success",
          title: "Address saved successfully!",
          showConfirmButton: false,
          timer: 2000, // 2 seconds
        });
        closeModal();
        window.location.reload();
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed to save address",
          text: response.data.message,
          showConfirmButton: false,
          timer: 2000, // 2 seconds
        });
      }
    } catch (err) {
      console.error("Error saving address:", err);
      Swal.fire({
        icon: "error",
        title: "Something went wrong",
        showConfirmButton: false,
        timer: 2000,
      });
    }
  };

  const inputClass = (field) =>
    `form-control ${errors[field] ? "border-danger" : ""}`;
  const selectClass = (field) =>
    `form-select ${errors[field] ? "border-danger" : ""}`;

  return (
    <div className="modal show d-block" tabIndex="-1">
      <div className="modal-dialog modal-lg modalwidth">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add New Address</h5>
            <button
              type="button"
              className="btn-close"
              onClick={closeModal}
            ></button>
          </div>
          <div
            className="modal-body"
            style={{ maxHeight: "800px", overflowY: "auto" }}
          >
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                {[
                  { name: "name", placeholder: "Name" },
                  { name: "mobile", placeholder: "Mobile Number" },
                  { name: "house_no", placeholder: "House No / Building Name" },
                  {
                    name: "road_name_area",
                    placeholder: "Road Name, Area, Colony",
                  },
                  { name: "land_mark", placeholder: "Landmark" },
                  { name: "pin_code", placeholder: "Pincode" },
                  { name: "district", placeholder: "District" },
                ].map((field) => (
                  <div className="col-12 col-md-6" key={field.name}>
                    <input
                      type="text"
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      className={inputClass(field.name)}
                      placeholder={field.placeholder}
                    />
                    {errors[field.name] && (
                      <small className="text-danger">
                        {errors[field.name]}
                      </small>
                    )}
                  </div>
                ))}

                <div className="col-md-6">
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className={selectClass("state")}
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option key={state.id} value={state.id}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                  {errors.state && (
                    <small className="text-danger">{errors.state}</small>
                  )}
                </div>
                <div className="col-md-6">
                  <select
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={selectClass("city")}
                  >
                    <option value="">Select City</option>
                    {cities.map((city) => (
                      <option key={city.id} value={city.name}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                  {errors.city && (
                    <small className="text-danger">{errors.city}</small>
                  )}
                </div>
              </div>

              {/* <div className="text-center mt-4">
                <input
                  type="submit"
                  className="submitbutton"
                  value="Save Address"
                />
              </div> */}
              <div className="text-center mt-4">
                <button type="submit" className="educate-btn sm">
                  Save Address
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressModal;
